<script context="module" lang="ts">
    export enum SentryEnvironment {
        STANDALONE = "standalone",
        SHARED = "shared",
    }
</script>

<script lang="ts">
    import * as Sentry from "@sentry/svelte";
    import {
        BrowserClient,
        defaultStackParser,
        getDefaultIntegrations,
        makeFetchTransport,
        Scope,
    } from "@sentry/browser";
    import { TokenStore } from "$/stores/TokenStore";
    import { TenantStore } from "$/stores/TenantStore";
    import { createEventDispatcher, onMount } from "svelte";
    import { ErrorBoundaryUtils } from "./ErrorBoundaryUtils";
    import { AppStore, AppType } from "$/stores/AppStore";
    import { ServiceConsts } from "$/services/ServiceConsts";

    const dispatch = createEventDispatcher();

    const SENTRY_DSN =
        "https://9f561771f7054908e831fff5168939ae@o4507588073095168.ingest.us.sentry.io/4507588127031296";

    let sentryClient;
    let sentryScope;

    export let sentryEnvironment: SentryEnvironment =
        SentryEnvironment.STANDALONE;

    const standaloneSentryInit = () => {
        if ($AppStore.sentry.isLoaded) return;

        Sentry.init({
            dsn: SENTRY_DSN,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration(),
            ],

            // Performance Monitoring
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            // tracePropagationTargets: ["http://localhost", ServiceConsts.SERVER_URL, ServiceConsts.SERVER_TEST_URL],

            // Session Replay
            replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });

        sentryScope = Sentry.getCurrentScope();

        $AppStore.sentry.isLoaded = true;
    };

    const sharedSentryInit = () => {
        const integrations = getDefaultIntegrations({}).filter(
            (defaultIntegration) => {
                return ![
                    "BrowserApiErrors",
                    "Breadcrumbs",
                    "GlobalHandlers",
                ].includes(defaultIntegration.name);
            },
        );
        sentryClient = new BrowserClient({
            dsn: SENTRY_DSN,
            integrations: integrations,
            transport: makeFetchTransport,
            stackParser: defaultStackParser,
        });

        sentryScope = new Scope();
        sentryScope.setClient(sentryClient);
        sentryClient.init();
    };

    onMount(() => {
        // Only initialize Sentry if we're not in DEV mode
        if (ServiceConsts.BUILD_MODE !== 'DEV') {
            if (sentryEnvironment === SentryEnvironment.STANDALONE) {
                standaloneSentryInit();
            } else {
                sharedSentryInit();
            }
        } else {
            console.log("Sentry initialization skipped in DEV mode");
        }
    });

    function shouldHandleError() {

        // Always handle errors for dashboard
        if($AppStore.type === AppType.DASHBOARD) {
            return true;
        }

        // There was a no better way of detecting if the error is from the extension or random website
        // because errors thrown by our scripts are often missing important information to determine their origins
        // so we're throwing and capturing a new error to get the stack trace and determine whether it's an
        // extension error or not.
        try {
            throw new Error();
        } catch (error) {
            const stack = error.stack;
            if (stack) {
                const regex = /at .*?\((.*?):\d+:\d+\)/g;
                let match;
                while ((match = regex.exec(stack)) !== null) {
                    const url = match[1];
                    if (url.includes(ServiceConsts.CHROME_EXTENSION_ID) || url.includes(ServiceConsts.EDGE_EXTENSION_ID)) {
                        return true;
                    }
                }
            }
        }
    }

    function handleOnError(event: Event) {

        if (!shouldHandleError()) return;

        // Check if we're not in DEV mode to determine whether to send to Sentry or just log
        if (ServiceConsts.BUILD_MODE !== 'DEV') {
            // Send to Sentry as normal
            if (sentryEnvironment === SentryEnvironment.STANDALONE) {
                ErrorBoundaryUtils.handleStandaloneError(
                    event,
                    sentryScope,
                    $TenantStore,
                    $TokenStore,
                );
            } else {
                ErrorBoundaryUtils.handleSharedError(
                    event,
                    sentryScope,
                    $TenantStore,
                    $TokenStore,
                );
            }
        } else {
            // Log to console instead
            const errorDetails = ErrorBoundaryUtils.serializeEvent(event);
            console.error("SENTRY CAPTURE (not sent in DEV mode):", {
                environment: sentryEnvironment,
                error: errorDetails,
                tenant: $TenantStore ? { 
                    id: $TenantStore.active?.id,
                    name: $TenantStore.active?.name 
                } : null,
                user: $TokenStore ? { 
                    id: $TokenStore.user?.id,
                    email: $TokenStore.user?.username 
                } : null,
                stackTrace: (event as ErrorEvent).error?.stack,
            });
        }

        dispatch("error", event);
    }
</script>

<svelte:window
    on:error|capture={handleOnError}
    on:unhandledrejection|capture={handleOnError}
/>
